import React from 'react';
import { Edit, Trash2, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatCurrency, formatStock } from '../../lib/utils';

export function MaterialsTable({ 
  materials, 
  filteredMaterials, 
  selectedMaterialFilter, 
  setSelectedMaterialFilter,
  exchangeRate,
  isSuperUser,
  onEdit,
  onDelete,
  onToggleDeduct
}) {
  return (
    <Card className="border-slate-200" data-testid="materials-table-card">
      <CardHeader className="border-b border-slate-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle>Materias Primas</CardTitle>
          <Select value={selectedMaterialFilter} onValueChange={setSelectedMaterialFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="material-filter-select">
              <SelectValue placeholder="Filtrar por material" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los materiales</SelectItem>
              {materials.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Material</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">Mínimo</TableHead>
                <TableHead className="text-center">Moneda</TableHead>
                <TableHead className="text-right">Costo Prom.</TableHead>
                <TableHead className="text-right">Valor Total</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-center">Descontar</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMaterials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    No hay materiales registrados
                  </TableCell>
                </TableRow>
              ) : (
                filteredMaterials.map((material) => (
                  <TableRow key={material.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{material.name}</TableCell>
                    <TableCell className="text-right">
                      <div>
                        <div>{formatStock(material.current_stock, material.unit)}</div>
                        {(material.unit || '').toLowerCase() === 'saco' && material.sack_weight > 0 && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            ≈ {(material.current_stock * material.sack_weight).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})} {material.sack_weight_unit || 'lb'}
                            <span className="text-slate-400"> ({material.sack_weight}{material.sack_weight_unit || 'lb'}/saco)</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatStock(material.min_stock, material.unit)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        material.currency === 'USD' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {material.currency === 'USD' ? 'US$' : 'RD$'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <div>{formatCurrency(material.average_cost, material.currency || 'DOP')}/{material.unit}</div>
                        {material.currency === 'USD' && (
                          <div className="text-xs text-green-600 font-medium">
                            (RD$ {(material.average_cost * exchangeRate).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}/{material.unit})
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      <div>
                        <div>{formatCurrency(material.total_value, material.currency || 'DOP')}</div>
                        {material.currency === 'USD' && (
                          <div className="text-xs text-green-600 font-medium">
                            (RD$ {(material.total_value * exchangeRate).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})})
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {material.current_stock <= material.min_stock ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Stock Bajo
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Normal
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={material.deduct_enabled || false}
                          onCheckedChange={(checked) => onToggleDeduct(material.id, checked)}
                          data-testid={`deduct-toggle-${material.id}`}
                          className="data-[state=checked]:bg-green-600"
                        />
                        <span className={`text-xs font-medium ${material.deduct_enabled ? 'text-green-600' : 'text-slate-400'}`}>
                          {material.deduct_enabled ? 'Sí' : 'No'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onEdit(material)}
                          className={`hover:bg-blue-50 ${isSuperUser ? 'text-blue-600 hover:text-blue-700' : 'text-slate-400 hover:text-slate-500'}`}
                          data-testid={`edit-material-${material.id}`}
                          title={isSuperUser ? 'Editar' : 'Requiere Super Usuario'}
                        >
                          <Edit className="h-4 w-4" />
                          {!isSuperUser && <Shield className="h-3 w-3 ml-0.5 text-amber-500" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onDelete(material.id)}
                          className={`hover:bg-red-50 ${isSuperUser ? 'text-red-600 hover:text-red-700' : 'text-slate-400 hover:text-slate-500'}`}
                          data-testid={`delete-material-${material.id}`}
                          title={isSuperUser ? 'Eliminar' : 'Requiere Super Usuario'}
                        >
                          <Trash2 className="h-4 w-4" />
                          {!isSuperUser && <Shield className="h-3 w-3 ml-0.5 text-amber-500" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
